import * as React from "react"
import type { ComponentProps } from "react"

import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { ChevronDown, Plus } from "lucide-react"
import { Command as CommandPrimitive } from "cmdk"

export type YearComboboxProps = {
  value?: string
  onChange: (v: string) => void
  onBlur?: () => void
  placeholder: string
  years: string[]
  triggerClassName?: string
  inputProps?: Omit<ComponentProps<typeof CommandPrimitive.Input>, "value" | "onChange" | "onBlur" | "name">
}

export const YearCombobox = ({
  value,
  onChange,
  onBlur,
  placeholder,
  years,
  triggerClassName,
  inputProps
}: YearComboboxProps) => {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const [commandValue, setCommandValue] = React.useState<string | undefined>()
  const { className: inputClassName, ...restInputProps } = inputProps ?? {}
  const trimmedSearch = React.useMemo(() => search.trim(), [search])
  const filteredYears = React.useMemo(
    () => (!trimmedSearch ? years : years.filter((year) => year.includes(trimmedSearch))),
    [trimmedSearch, years]
  )
  const customYear = trimmedSearch || null
  const canAddCustomYear = !!customYear && !years.includes(customYear)

  React.useEffect(() => {
    if (!open) {
      setSearch("")
      setCommandValue(undefined)
    }
  }, [open])

  const commit = (y: string) => {
    onChange(y)
    setOpen(false)
    setCommandValue(undefined)
    onBlur?.()
  }

  React.useEffect(() => {
    if (!open) return

    if (trimmedSearch) {
      setCommandValue(filteredYears[0])
      return
    }

    if (value && years.includes(value)) {
      setCommandValue(value)
      return
    }

    setCommandValue(filteredYears[0])
  }, [filteredYears, open, trimmedSearch, value, years])

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn("w-64 justify-between", !value && "text-muted-foreground", triggerClassName)}
          >
            <span className="truncate text-left">{value || placeholder}</span>
            <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start" side="bottom">
          <Command shouldFilter={false} value={commandValue} onValueChange={setCommandValue}>
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <CommandPrimitive.Input
                value={search}
                onValueChange={setSearch}
                placeholder="Type or search a year"
                className={cn(
                  "flex-1 bg-transparent text-sm outline-hidden placeholder:text-muted-foreground",
                  inputClassName
                )}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && customYear && canAddCustomYear) {
                    event.preventDefault()
                    commit(customYear)
                  }
                }}
                {...restInputProps}
              />
              {customYear && canAddCustomYear ? (
                <Button type="button" variant="ghost" size="icon" onClick={() => commit(customYear)}>
                  <Plus className="size-4" />
                </Button>
              ) : null}
            </div>
            <CommandList className="max-h-24">
              <CommandEmpty>No matching years</CommandEmpty>
              {filteredYears.map((y) => (
                <CommandItem key={y} value={y} onSelect={() => commit(y)}>
                  {y}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
